import { Check, ChevronDown, ChevronUp, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { AddLeadDialog } from '@/modules/marketing/components/leads/AddLeadDialog'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { PlaceholderPage } from '@/shared/components/ui/PlaceholderPage'

import LeadSourceSummary from '../components/leads/LeadSourceSummary'

import AdminLeadsPage from './AdminLeadsPage'

const SOURCES = [
  { label: 'All Sources', value: 'all' },
  { label: 'LinkedIn', value: 'linkedin' },
  { label: 'Facebook', value: 'facebook' },
  { label: 'Offline', value: 'offline' },
]

export default function AdminLeadsRoute() {
  const [searchParams, setSearchParams] = useSearchParams()
  const sourceParam = searchParams.get('source') || 'all'
  const statusParam = searchParams.get('status') || ''
  const warmthParam = searchParams.get('leadWarmth') || ''

  const [source, setSource] = useState(sourceParam)
  const [open, setOpen] = useState(false)
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false)
  const [selectedLead, setSelectedLead] = useState(null)
  const [listEpoch, setListEpoch] = useState(0)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSource(sourceParam)
  }, [sourceParam])

  const handleSelectSource = (value) => {
    setSource(value)
    const nextParams = new URLSearchParams(searchParams)
    if (value === 'all') {
      nextParams.delete('source')
    } else {
      nextParams.set('source', value)
    }
    setSearchParams(nextParams)
  }

  const selectedSource =
    SOURCES.find((item) => item.value === source)?.label || 'All Sources'

  return (
    <div className="space-y-6">
      <PlaceholderPage
        title="Leads"
        description="Manage marketing leads."
        action={
          <div className="flex items-center gap-3">
            <Button
              size="lg"
              className="rounded-full bg-[#0F172A] px-5 text-white hover:bg-[#1E293B]"
              onClick={() => {
                setSelectedLead(null)
                setOpen(true)
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Lead
            </Button>

            <DropdownMenu
              open={sourceDropdownOpen}
              onOpenChange={setSourceDropdownOpen}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-2xl bg-white"
                >
                  {selectedSource}
                  {sourceDropdownOpen ? (
                    <ChevronUp className="ml-2 h-4 w-4" />
                  ) : (
                    <ChevronDown className="ml-2 h-4 w-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Filter by source</DropdownMenuLabel>

                {SOURCES.map((item) => (
                  <DropdownMenuItem
                    key={item.value}
                    onSelect={() => handleSelectSource(item.value)}
                    className={`flex items-center justify-between ${
                      source === item.value ? 'bg-blue-50 text-blue-600' : ''
                    }`}
                  >
                    {item.label}

                    {source === item.value && <Check className="h-4 w-4" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      <LeadSourceSummary
        activeSource={source}
        onSelectSource={handleSelectSource}
      />

      <AdminLeadsPage
        key={listEpoch}
        source={source}
        status={statusParam}
        leadWarmth={warmthParam}
      />
      <AddLeadDialog
        open={open}
        onOpenChange={setOpen}
        lead={selectedLead}
        module="admin"
        onSuccess={() => setListEpoch((value) => value + 1)}
      />
    </div>
  )
}
